/**
 * CANONICAL MODULE — app-platform/shared/authFlags.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/authFlags.ts`). It imports nothing from
 * any app; the only app-specific facts — the storage keys — are passed to `configureAuthFlags()`.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place** — that is exactly how
 * `bundle-budget.mjs` became three different files, each with a safeguard the other two never got.
 *
 * ── What it is for ───────────────────────────────────────────────────────────────────────────────
 * Three flags decide whether a signed-out visitor is bounced straight back to Microsoft, shown a
 * landing page, or shown a sign-in button. They look similar and they are **not interchangeable**.
 * Merging any two of them reproduces a bug that has already shipped in this estate:
 *
 *   returningVisitor    localStorage    permanent            this device has signed in before
 *   deliberateSignOut   localStorage    standing instruction the user chose to sign out
 *   autoLoginAttempted  sessionStorage  one tab session      we already bounced this tab
 *
 * Two rules that are not obvious and cost real bugs to learn:
 *
 * **1. `autoLoginAttempted` must survive sign-out.** Clearing it on sign-out was bindr's finding #25:
 * the logout redirect is itself a fresh page load, which then found no attempt recorded and bounced
 * the user straight back out to Microsoft. bindr's own sign-in screen became unreachable.
 *
 * **2. `deliberateSignOut` must be written BEFORE the logout redirect fires**, because the redirect
 * never returns into the function that called it. It is lifted only by an explicit `signIn()` — the
 * automatic bounce must never lift it, or it means nothing.
 *
 * ── Key names ────────────────────────────────────────────────────────────────────────────────────
 * Each app passes its own existing key spellings. **Do not "standardise" the key names**: a renamed
 * key reads as a brand-new device for every existing install, which demotes a returning user to the
 * landing page and re-arms auto-login for someone who had deliberately signed out.
 *
 *     configureAuthFlags({
 *       returningVisitor: 'bindr-signed-in-before-v1',
 *       deliberateSignOut: 'bindr-signed-out-v1',
 *       autoLoginAttempted: 'bindr-auto-signin-attempted',
 *     })
 *
 * Every read and write is wrapped: storage throws in private modes and with site data blocked, and
 * these are called from boot paths that must not crash. A failed read answers "no".
 */

export interface AuthFlagKeys {
  /** localStorage. Permanent: this device has completed a sign-in at least once. */
  returningVisitor: string
  /** localStorage. A standing instruction, lifted only by an explicit sign-in. */
  deliberateSignOut: string
  /** sessionStorage. One auto-login bounce per tab session. */
  autoLoginAttempted: string
}

let keys: AuthFlagKeys | null = null

export function configureAuthFlags(next: AuthFlagKeys): void {
  keys = next
}

/** @internal Test seam (forget the keys, as a fresh page would). Not called by app code. */
export function resetAuthFlags(): void {
  keys = null
}

function requireKeys(): AuthFlagKeys {
  if (!keys) {
    throw new Error('authFlags: configureAuthFlags() must be called before any flag is read or written')
  }
  return keys
}

function readLocal(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeLocal(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, '1')
    else localStorage.removeItem(key)
  } catch {
    /* storage unavailable — the flag is an optimisation, never a correctness requirement */
  }
}

/* ── returningVisitor ─────────────────────────────────────────────────────────────────────────── */

/**
 * Has this device ever completed a sign-in? Gates the landing page vs the sign-in screen, and gates
 * auto-login so a brand-new visitor is never bounced to Microsoft before seeing the app at all.
 *
 * Never cleared — not by sign-out, not by erase. "Have we met" stays true once it is true.
 */
export function isReturningVisitor(): boolean {
  return readLocal(requireKeys().returningVisitor)
}

/** Call when a sign-in has actually completed, not when one is started. */
export function markReturningVisitor(): void {
  writeLocal(requireKeys().returningVisitor, true)
}

/* ── deliberateSignOut ────────────────────────────────────────────────────────────────────────── */

/**
 * Did the user choose to sign out? If so, no automatic bounce may sign them back in — otherwise
 * signing out and reopening the tab silently signs you back in, which is not a sign-out.
 */
export function hasSignedOutDeliberately(): boolean {
  return readLocal(requireKeys().deliberateSignOut)
}

/** MUST be called before `logoutRedirect()` — the redirect never returns into the caller. */
export function markSignedOut(): void {
  writeLocal(requireKeys().deliberateSignOut, true)
}

/** Called from an explicit `signIn()` only. The automatic bounce must never call this. */
export function clearSignedOut(): void {
  writeLocal(requireKeys().deliberateSignOut, false)
}

/* ── autoLoginAttempted ───────────────────────────────────────────────────────────────────────── */

/**
 * Have we already tried the automatic bounce in this tab session? One per tab, so a persistently
 * failing sign-in cannot loop redirect → back → redirect.
 *
 * Deliberately NOT cleared by sign-out — see rule 1 in the header.
 *
 * **This one fails CLOSED**, unlike the two above: a `sessionStorage` that throws (private mode, a
 * locked-down profile) reads as "already tried". The other two flags fail open because the cost is
 * one extra click or one extra silent bounce; the cost here is an infinite redirect loop, because a
 * guard that cannot remember it fired is not a guard at all. Bindr had this right and W9's first
 * draft of this module did not — found during the bindr rollout, 2026-09-19.
 */
export function hasAttemptedAutoLogin(): boolean {
  try {
    return sessionStorage.getItem(requireKeys().autoLoginAttempted) === '1'
  } catch {
    return true
  }
}

/** Call immediately before starting the bounce, not after it returns — it does not return. */
export function markAutoLoginAttempted(): void {
  try {
    sessionStorage.setItem(requireKeys().autoLoginAttempted, '1')
  } catch {
    /* storage unavailable — see writeLocal */
  }
}
